import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useToast } from '../hooks/use-toast';

const ContactPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission - will be replaced with actual API call
    setTimeout(() => {
      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. We'll get back to you soon."
      });
      setFormData({ name: '', email: '', company: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container-main">
          <div className="glass-card hero-content-card animate-fade-in">
            <h1 className="heading-1 text-brand-primary mb-6">Let's Discuss Your Project</h1>
            <p className="body-large text-text-primary max-w-3xl">
              Ready to bring your design vision to life? Get in touch with our team
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding bg-page-dark">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="glass-card contact-info-card animate-slide-up" style={{ animationDelay: '0s' }}>
                <h3 className="heading-4 text-brand-primary mb-6">Contact Information</h3>
                
                <div className="space-y-6">
                  <div className="contact-item">
                    <div className="contact-icon-wrapper-page">
                      <Mail size={24} />
                    </div>
                    <div>
                      <h4 className="heading-6 text-brand-primary mb-1">Email</h4>
                      <a href={`mailto:${companyInfo.email}`} className="body-medium text-text-secondary hover:text-brand-primary transition-colors">
                        {companyInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon-wrapper-page">
                      <Phone size={24} />
                    </div>
                    <div>
                      <h4 className="heading-6 text-brand-primary mb-1">Phone</h4>
                      <a href={`tel:${companyInfo.phone}`} className="body-medium text-text-secondary hover:text-brand-primary transition-colors">
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="contact-item">
                    <div className="contact-icon-wrapper-page">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <h4 className="heading-6 text-brand-primary mb-1">Address</h4>
                      <p className="body-medium text-text-secondary">{companyInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-card business-hours-card animate-slide-up" style={{ animationDelay: '0.1s' }}>
                <h4 className="heading-6 text-brand-primary mb-4">Business Hours</h4>
                <p className="body-medium text-text-secondary">
                  Monday - Friday: 9:00 AM - 6:00 PM<br />
                  Saturday: 10:00 AM - 2:00 PM<br />
                  Sunday: Closed
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="glass-card contact-form-card animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <h3 className="heading-4 text-brand-primary mb-6">Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="label-text">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="input-field-page"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="label-text">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="input-field-page"
                    placeholder="your.email@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="label-text">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="input-field-page"
                    placeholder="Your company name"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="label-text">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="input-field-page resize-none"
                    placeholder="Tell us about your project..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-2 btn-glow"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send size={20} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
